'use client';

import { useDispatch } from 'react-redux';
import { redirect } from 'next/navigation';
import { setIsSignUp, setRequestAction } from '@/features/auth/authSlice';
import { FREE_TRIAL_DAYS } from '@/config/consts';
import NeuralBackground from './NeuralBackground';
import FinkOrbPreview from './FinkOrbPreview';
import Image from 'next/image';

export default function HeroSection() {
  const dispatch = useDispatch();

  const handleFreeTrial = () => {
    dispatch(setRequestAction('FREE_TRIAL'));
    dispatch(setIsSignUp(true));
    redirect('/auth/login');
  };

  const handleDemo = () => {
    // Scroll to Fink AI demo section
    const demoSection = document.getElementById('fink-ai-demo');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-[80vh] lg:min-h-[90vh] flex items-center overflow-hidden">
      <NeuralBackground />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Content */}
          <div className="text-center lg:text-left">
            {/* AI Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0D60E6] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0D60E6]"></span>
              </span>
              <span className="text-sm font-medium text-[#0D52C2]">
                Powered by Advanced AI
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#181D27] mb-6 leading-tight">
              Your AI-Powered Partner for
              <br />
              <span className="text-[#0D60E6]">Tax Compliance</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
              Finkr helps you manage tax notices, appeals, and compliance with built-in AI.
              Ask Fink, our AI assistant, anything about your clients.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4 mb-6 lg:mb-8">
              <button
                onClick={handleFreeTrial}
                className="group w-full sm:w-auto h-12 lg:h-14 px-6 lg:px-8 bg-gradient-to-r from-[#0D60E6] to-[#0D52C2] text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 touch-manipulation whitespace-nowrap"
              >
                <span className="text-sm lg:text-base">Try Finkr Free</span>
                <span className="text-lg group-hover:translate-x-1 transition-transform">→</span>
              </button>
              <button
                onClick={handleDemo}
                className="w-full sm:w-auto h-12 lg:h-14 px-6 lg:px-8 bg-white border-2 border-gray-200 text-gray-700 font-bold rounded-xl hover:border-[#0D60E6] hover:bg-blue-50 transition-all duration-300 flex items-center justify-center gap-2 touch-manipulation whitespace-nowrap"
              >
                <Image src="/assets/play-circle.svg" alt="Play" width={20} height={20} className="flex-shrink-0" />
                <span className="text-sm lg:text-base">See Finkr in Action</span>
              </button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 lg:gap-6 text-xs lg:text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 lg:w-5 lg:h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{FREE_TRIAL_DAYS}-day free trial</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 lg:w-5 lg:h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>No credit card</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 lg:w-5 lg:h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>

          {/* Right - Fink AI Visual */}
          <div className="flex justify-center lg:justify-center">
            <div className="relative lg:-ml-12">
              {/* Floating feature cards around the orb */}
              <div className="absolute -top-12 -right-36 p-4 bg-white rounded-xl shadow-lg border border-gray-100 animate-float hidden lg:block" style={{ animationDelay: '0s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#0D60E6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Document Analysis</div>
                    <div className="text-xs text-gray-500">AI-powered scanning</div>
                  </div>
                </div>
              </div>

              <div className="absolute -top-16 -left-44 p-4 bg-white rounded-xl shadow-lg border border-gray-100 animate-float hidden lg:block" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#0D60E6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Natural Language</div>
                    <div className="text-xs text-gray-500">Ask anything</div>
                  </div>
                </div>
              </div>

              <div className="absolute top-1/2 -right-32 -translate-y-1/2 p-4 bg-white rounded-xl shadow-lg border border-gray-100 animate-float hidden lg:block" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#0D60E6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Instant Responses</div>
                    <div className="text-xs text-gray-500">Generate in seconds</div>
                  </div>
                </div>
              </div>

              {/* AI Insights Card - Bottom Left */}
              <div className="absolute bottom-16 -left-40 p-4 bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl shadow-lg border border-violet-200 animate-float hidden lg:block" style={{ animationDelay: '1.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800">AI Insights</div>
                    <div className="text-xs text-violet-600">Client health reports</div>
                  </div>
                </div>
              </div>

              {/* Main Orb */}
              <FinkOrbPreview size="xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </section>
  );
}
